import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  IonAvatar,
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSearchbar,
  IonSpinner,
  IonToggle,
  useIonAlert,
} from "@ionic/react";
import {
  arrowBackOutline,
  trashOutline,
  warningOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
} from "ionicons/icons";
import { collectionGroup, getDocs, query } from "firebase/firestore";
import { db } from "../firebase";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";
import { AuthContext } from "../context/AuthContext";
import { isDeleteUserAdmin } from "../constants/adminAllowlist";
import "./DeleteUserDocs.css";
import {
  DanglingRef,
  DocEntry,
  SchedulerJobGroup,
  UserDeletionSummary,
  deleteDocsByPath,
  deleteEntireUser,
  deleteNestedField,
  deleteStorageFiles,
  deleteUserSubcollection,
  fetchUserDeletionSummary,
  removeFromArrayField,
} from "./deleteUserDocsUtils";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface UserResult {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  occupation: string;
  profilePicURL: string;
  isScheduler: boolean;
}

// ---------------------------------------------------------------------------
// Main component (access-gated wrapper)
// ---------------------------------------------------------------------------

export const DeleteUserDocs: React.FC = () => {
  const { currentUser } = useContext(AuthContext) as any;
  const adminUid: string = currentUser?.uid || "";
  const isAuthorized = isDeleteUserAdmin(adminUid);

  if (!isAuthorized) {
    return (
      <div style={{ padding: 24, maxWidth: 600 }}>
        <IonCard color="warning">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={warningOutline} /> Access Denied
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <p>
              Your UID is not on the allowlist for the "Delete User Docs"
              admin process.
            </p>
            <p style={{ fontFamily: "monospace", fontSize: "0.85em" }}>
              Current UID: <b>{adminUid || "(not signed in)"}</b>
            </p>
            <p style={{ marginTop: 12 }}>
              To authorize an admin, add their UID to{" "}
              <code>src/constants/adminAllowlist.ts</code>.
            </p>
          </IonCardContent>
        </IonCard>
      </div>
    );
  }

  return <DeleteUserDocsInner adminUid={adminUid} />;
};

// ---------------------------------------------------------------------------
// Inner component (hooks-safe — only mounted when authorized)
// ---------------------------------------------------------------------------

const DeleteUserDocsInner: React.FC<{ adminUid: string }> = ({ adminUid }) => {
  const [present] = useIonAlert();

  // User list + search state
  const [allUsers, setAllUsers] = useState<UserResult[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");

  // Selected user + summary
  const [selected, setSelected] = useState<UserResult | null>(null);
  const [summary, setSummary] = useState<UserDeletionSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Controls
  const [dryRun, setDryRun] = useState(true);
  const [confirmInput, setConfirmInput] = useState("");
  const [busy, setBusy] = useState(false);


  // -------------------------------------------------------------------------
  // Load all users once
  // -------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const snap = await getDocs(
          query(collectionGroup(db, FB_COLLECTION_PATH.Users))
        );
        const results: UserResult[] = [];
        const seen = new Set<string>();
        snap.forEach((d) => {
          const data = d.data() || {};
          const uid = data.uid || d.id;
          if (!uid || seen.has(uid)) return;
          seen.add(uid);
          results.push({
            uid,
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            company: data.company || "",
            occupation: data.occupation || "",
            profilePicURL: data.profilePicURL || "",
            isScheduler: !!data.isScheduler,
          });
        });
        results.sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`
          )
        );
        if (!cancelled) setAllUsers(results);
      } catch (err) {
        console.error("Load users failed:", err);
        if (!cancelled) setUsersError((err as any)?.message || String(err));
      }
      if (!cancelled) setUsersLoading(false);
    };
    void loadUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------------------------
  // Derived filtered list
  // -------------------------------------------------------------------------
  const filteredUsers = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return allUsers;
    return allUsers.filter((u) => {
      const full = `${u.firstName} ${u.lastName}`.toLowerCase();
      return (
        u.firstName.toLowerCase().includes(term) ||
        u.lastName.toLowerCase().includes(term) ||
        full.includes(term) ||
        u.company.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.uid === searchText.trim()
      );
    });
  }, [allUsers, searchText]);


  // -------------------------------------------------------------------------
  // Summary fetch
  // -------------------------------------------------------------------------
  const loadSummary = async (user: UserResult) => {
    setSummaryLoading(true);
    setSummary(null);
    try {
      const s = await fetchUserDeletionSummary(user.uid);
      setSummary(s);
    } catch (err) {
      console.error("fetchUserDeletionSummary failed:", err);
      present({
        header: "Error",
        message: `Failed to load user summary: ${
          (err as any)?.message || err
        }`,
        buttons: ["OK"],
      });
    }
    setSummaryLoading(false);
  };

  useEffect(() => {
    if (selected) {
      loadSummary(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // -------------------------------------------------------------------------
  // Deletion helpers
  // -------------------------------------------------------------------------
  const requireConfirm = (): boolean => {
    if (!selected) return false;
    const expected = selected.email || selected.uid;
    if (confirmInput.trim() !== expected) {
      present({
        header: "Confirmation required",
        message: `Type the user's email (${selected.email}) or UID (${selected.uid}) in the confirmation box to proceed.`,
        buttons: ["OK"],
      });
      return false;
    }
    return true;
  };

  const baseOpts = () => {
    if (!selected) throw new Error("No user selected");
    return {
      dryRun,
      adminUid,
      targetUid: selected.uid,
      action: "admin",
    };
  };

  const runWithBusy = async (
    fn: () => Promise<void>,
    successMsg: string
  ) => {
    setBusy(true);
    try {
      await fn();
      if (selected) await loadSummary(selected);
      present({
        header: dryRun ? "Dry run complete" : "Done",
        message: successMsg,
        buttons: ["OK"],
      });
    } catch (err) {
      console.error("Delete action failed:", err);
      present({
        header: "Error",
        message: `Action failed: ${(err as any)?.message || err}`,
        buttons: ["OK"],
      });
    }
    setBusy(false);
  };

  const confirmAndDelete = (
    header: string,
    message: string,
    fn: () => Promise<void>,
    requireTyped = false
  ) => {
    if (requireTyped && !requireConfirm()) return;
    present({
      header,
      message: dryRun
        ? `(DRY RUN — no changes will be written) ${message}`
        : message,
      buttons: [
        "Cancel",
        {
          text: dryRun ? "Preview" : "Delete",
          role: "destructive",
          handler: () => {
            void runWithBusy(fn, header + " — completed.");
          },
        },
      ],
    });
  };

  // -------------------------------------------------------------------------
  // Per-doc / per-section handlers
  // -------------------------------------------------------------------------
  const handleDeleteSinglePath = (path: string) => {
    confirmAndDelete(
      "Delete single document",
      `Path: ${path}`,
      async () => {
        await deleteDocsByPath([path], {
          ...baseOpts(),
          action: "singleDoc",
        });
      }
    );
  };

  const handleDeleteSubcollection = (name: string) => {
    if (!summary) return;
    confirmAndDelete(
      `Delete users/${summary.uid}/${name}`,
      `Will delete ${summary.subcollections[name]?.length || 0} docs.`,
      async () => {
        await deleteUserSubcollection(summary.uid, name, {
          ...baseOpts(),
          action: "subcollection",
        });
      },
      true
    );
  };

  const handleDeleteJobGroup = (group: SchedulerJobGroup) => {
    if (!summary) return;
    const paths = [
      ...(group.schedulerCopy ? [group.schedulerCopy.path] : []),
      ...group.werkerCopies.map((w) => w.path),
    ];
    confirmAndDelete(
      `Delete job: ${group.jobTitle || group.uniqueJobId}`,
      `Will delete ${paths.length} jobsDocs copies (scheduler + all werkers).`,
      async () => {
        await deleteDocsByPath(paths, {
          ...baseOpts(),
          action: "schedulerJobGroup",
        });
      }
    );
  };

  const handleDeleteAllSchedulerJobs = () => {
    if (!summary) return;
    const paths: string[] = [];
    for (const g of summary.schedulerJobs) {
      if (g.schedulerCopy) paths.push(g.schedulerCopy.path);
      for (const w of g.werkerCopies) paths.push(w.path);
    }
    confirmAndDelete(
      "Delete ALL scheduler jobs",
      `Will delete ${paths.length} jobsDocs copies across ${summary.schedulerJobs.length} jobs.`,
      async () => {
        await deleteDocsByPath(paths, {
          ...baseOpts(),
          action: "allSchedulerJobs",
        });
      },
      true
    );
  };

  const handleDeleteAllWerkerJobs = () => {
    if (!summary) return;
    const paths = summary.werkerJobs.map((d) => d.path);
    confirmAndDelete(
      "Delete ALL werker jobs",
      `Will delete ${paths.length} jobsDocs copies where user is werker.`,
      async () => {
        await deleteDocsByPath(paths, {
          ...baseOpts(),
          action: "allWerkerJobs",
        });
      },
      true
    );
  };

  const handleDeleteAssociateKey = (associateUid: string) => {
    if (!summary?.associatesDoc) return;
    const path = summary.associatesDoc.path;
    confirmAndDelete(
      `Remove associate ${associateUid}`,
      `From ${path}.`,
      async () => {
        await deleteNestedField(path, associateUid, {
          ...baseOpts(),
          action: "associateKey",
        });
      }
    );
  };

  const handleDeleteAssociatesDoc = () => {
    if (!summary?.associatesDoc) return;
    const path = summary.associatesDoc.path;
    confirmAndDelete(
      "Delete entire userAssociations doc",
      `Will delete ${path}.`,
      async () => {
        await deleteDocsByPath([path], {
          ...baseOpts(),
          action: "associatesDoc",
        });
      },
      true
    );
  };

  const handleDeleteChatsDoc = () => {
    if (!summary?.chatsDoc) return;
    confirmAndDelete(
      "Delete chats summary doc",
      `Will delete ${summary.chatsDoc.path}. (Message subcollections under other chat docs are not touched here.)`,
      async () => {
        await deleteDocsByPath([summary.chatsDoc!.path], {
          ...baseOpts(),
          action: "chatsDoc",
        });
      },
      true
    );
  };

  const handleCleanupDanglingRef = (ref: DanglingRef) => {
    confirmAndDelete(
      `Clean up: ${ref.description}`,
      `Target: ${ref.targetPath}`,
      async () => {
        if (ref.cleanup.type === "deleteField") {
          await deleteNestedField(
            ref.targetPath,
            ref.cleanup.fieldPath,
            { ...baseOpts(), action: `dangling:${ref.kind}` }
          );
        } else {
          await removeFromArrayField(
            ref.targetPath,
            ref.cleanup.fieldPath,
            ref.cleanup.value,
            { ...baseOpts(), action: `dangling:${ref.kind}` }
          );
        }
      }
    );
  };

  const handleCleanupAllDangling = () => {
    if (!summary || summary.danglingRefs.length === 0) return;
    confirmAndDelete(
      "Clean up ALL dangling references",
      `Will modify ${summary.danglingRefs.length} other documents.`,
      async () => {
        for (const r of summary.danglingRefs) {
          if (r.cleanup.type === "deleteField") {
            await deleteNestedField(r.targetPath, r.cleanup.fieldPath, {
              ...baseOpts(),
              action: `dangling:${r.kind}`,
            });
          } else {
            await removeFromArrayField(
              r.targetPath,
              r.cleanup.fieldPath,
              r.cleanup.value,
              { ...baseOpts(), action: `dangling:${r.kind}` }
            );
          }
        }
      },
      true
    );
  };

  const handleDeleteStorage = () => {
    if (!summary || summary.storageFiles.length === 0) return;
    confirmAndDelete(
      "Delete ALL discovered Storage files",
      `Will delete ${summary.storageFiles.length} files.`,
      async () => {
        await deleteStorageFiles(summary.storageFiles, {
          ...baseOpts(),
          action: "storageAll",
        });
      },
      true
    );
  };

  const handleDeleteEntireUser = () => {
    if (!summary) return;
    confirmAndDelete(
      "DELETE ENTIRE USER",
      `This will delete ALL Firestore docs (user root + subcollections + jobs + associates + chats summary + announcements + FCM tokens + storage files) and clean up dangling references. Firebase Auth user must be removed manually via the Firebase console.`,
      async () => {
        await deleteEntireUser(summary, {
          ...baseOpts(),
          action: "deleteEntireUser",
        });
      },
      true
    );
  };

  // -------------------------------------------------------------------------
  // Derived counts for summary card
  // -------------------------------------------------------------------------
  const totalDocs = useMemo(() => {
    if (!summary) return 0;
    let n = 0;
    if (summary.userRoot) n += 1;
    if (summary.userSettings) n += 1;
    for (const v of Object.values(summary.subcollections)) n += v.length;
    for (const g of summary.schedulerJobs) {
      if (g.schedulerCopy) n += 1;
      n += g.werkerCopies.length;
    }
    n += summary.werkerJobs.length;
    if (summary.associatesDoc) n += 1;
    if (summary.chatsDoc) n += 1;
    n += summary.announcementsAuthored.length;
    n += summary.crewsMember.length;
    n += summary.fcmTokens.length;
    n += summary.danglingRefs.length;
    n += summary.storageFiles.length;
    return n;
  }, [summary]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="delete-user-docs" style={{ padding: 16, maxWidth: 1100 }}>
      <h2 style={{ margin: "0 0 12px 0" }}>
        <IonIcon icon={trashOutline} /> Delete User Docs
      </h2>

      {/* Global controls */}
      <IonCard>
        <IonCardContent>
          <IonItem lines="none" className="delete-user-docs__dry-run-item">
            <IonCheckbox
              slot="start"
              color="primary"
              className="delete-user-docs__dry-run-checkbox"
              checked={dryRun}
              onIonChange={(e) => setDryRun(!!e.detail.checked)}
            />
            <IonLabel className="delete-user-docs__dry-run-label">
              <strong>Dry run</strong> — preview without writing (recommended
              first)
            </IonLabel>
          </IonItem>
          <IonNote color="medium" style={{ display: "block", marginTop: 4 }}>
            Admin: <code>{adminUid}</code>
          </IonNote>
        </IonCardContent>
      </IonCard>

      {/* User list + search */}
      {!selected && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <h3 style={{ margin: 0 }} className="delete-user-docs__all-users-title">
              All users
            </h3>
            <IonBadge className="delete-user-docs__all-users-count">
              {usersLoading ? "…" : filteredUsers.length}
              {searchText.trim() && !usersLoading
                ? ` of ${allUsers.length}`
                : ""}
            </IonBadge>
          </div>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => setSearchText(e.detail.value || "")}
            placeholder="Filter by name, company, email, or UID"
            debounce={150}
          />

          {usersLoading && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <IonSpinner name="crescent" />
              <p style={{ color: "#888" }}>Loading users…</p>
            </div>
          )}

          {usersError && (
            <IonNote color="danger" style={{ display: "block", marginTop: 8 }}>
              Failed to load users: {usersError}
            </IonNote>
          )}

          {!usersLoading && !usersError && (
            <div
              style={{
                marginTop: 8,
                maxHeight: "60vh",
                overflowY: "auto",
                border: "1px solid var(--ion-border-color, #e0e0e0)",
                borderRadius: 6,
              }}
            >
              <IonList>
                {filteredUsers.map((u) => (
                  <IonItem
                    key={u.uid}
                    button
                    onClick={() => {
                      setSelected(u);
                      setConfirmInput("");
                    }}
                  >
                    <IonAvatar slot="start">
                      <img
                        src={
                          u.profilePicURL ||
                          "/assets/icon/default-avatar.png"
                        }
                        alt=""
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/assets/icon/default-avatar.png";
                        }}
                      />
                    </IonAvatar>
                    <IonLabel>
                      <h2>
                        {u.firstName} {u.lastName}
                        {u.isScheduler && (
                          <IonBadge
                            color="tertiary"
                            style={{ marginLeft: 8 }}
                          >
                            scheduler
                          </IonBadge>
                        )}
                      </h2>
                      <p>
                        {u.email}
                        {u.company ? ` — ${u.company}` : ""}
                      </p>
                      <p
                        style={{
                          fontFamily: "monospace",
                          fontSize: "0.75em",
                        }}
                      >
                        {u.uid}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
                {filteredUsers.length === 0 && (
                  <IonItem lines="none">
                    <IonLabel
                      style={{ textAlign: "center", color: "#999" }}
                    >
                      No users match "{searchText}".
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </div>
          )}
        </div>
      )}

      {/* Selected user summary */}
      {selected && (
        <div style={{ marginTop: 16 }}>
          <IonButton
            fill="clear"
            onClick={() => {
              setSelected(null);
              setSummary(null);
              setConfirmInput("");
            }}
          >
            <IonIcon icon={arrowBackOutline} slot="start" />
            Back to search
          </IonButton>

          <IonCard>
            <IonCardContent>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <IonAvatar style={{ width: 64, height: 64 }}>
                  <img
                    src={
                      selected.profilePicURL ||
                      "/assets/icon/default-avatar.png"
                    }
                    alt=""
                  />
                </IonAvatar>
                <div>
                  <h2 style={{ margin: 0 }}>
                    {selected.firstName} {selected.lastName}
                  </h2>
                  <p style={{ margin: 0, color: "#666" }}>{selected.email}</p>
                  <p
                    style={{
                      margin: 0,
                      fontFamily: "monospace",
                      fontSize: "0.8em",
                    }}
                  >
                    {selected.uid}
                  </p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Confirmation input */}
          <IonCard>
            <IonCardContent>
              <IonLabel position="stacked">
                Type the user's email or UID to enable destructive actions:
              </IonLabel>
              <IonInput
                value={confirmInput}
                onIonChange={(e) => setConfirmInput(e.detail.value || "")}
                placeholder={selected.email || selected.uid}
              />
              {confirmInput.trim() ===
              (selected.email || selected.uid) ? (
                <IonNote color="success">
                  <IonIcon icon={checkmarkCircleOutline} /> Confirmation matched
                </IonNote>
              ) : (
                <IonNote color="medium">
                  Matches email or UID unlocks section- and user-level deletes.
                </IonNote>
              )}
            </IonCardContent>
          </IonCard>

          {summaryLoading && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <IonSpinner name="crescent" />
              <p>Loading associated documents…</p>
            </div>
          )}

          {!summaryLoading && summary && (
            <>
              <IonCard>
                <IonCardContent>
                  <h3>
                    Summary:{" "}
                    <IonBadge color="tertiary">{totalDocs}</IonBadge> associated
                    items
                  </h3>
                  <IonButton
                    color="danger"
                    disabled={busy}
                    onClick={handleDeleteEntireUser}
                  >
                    <IonIcon icon={trashOutline} slot="start" />
                    Delete Entire User
                  </IonButton>
                </IonCardContent>
              </IonCard>

              {/* Sections */}
              <Section
                title="User root document"
                count={summary.userRoot ? 1 : 0}
              >
                {summary.userRoot && (
                  <DocRow
                    path={summary.userRoot.path}
                    label="users root"
                    onDelete={() =>
                      handleDeleteSinglePath(summary.userRoot!.path)
                    }
                    busy={busy}
                  />
                )}
                <p style={{ color: "#888", fontSize: "0.85em" }}>
                  The root doc is deleted LAST by "Delete Entire User" to avoid
                  orphaning subcollections.
                </p>
              </Section>

              <Section
                title="userSettings"
                count={summary.userSettings ? 1 : 0}
              >
                {summary.userSettings && (
                  <DocRow
                    path={summary.userSettings.path}
                    onDelete={() =>
                      handleDeleteSinglePath(summary.userSettings!.path)
                    }
                    busy={busy}
                  />
                )}
              </Section>

              <Section
                title="User subcollections"
                count={Object.values(summary.subcollections).reduce(
                  (n, v) => n + v.length,
                  0
                )}
              >
                {Object.keys(summary.subcollections).length === 0 && (
                  <IonNote color="medium">No subcollection docs found.</IonNote>
                )}
                {Object.entries(summary.subcollections).map(
                  ([name, docs]) => (
                    <div key={name} style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <strong>{name}</strong>
                        <CountChip>{docs.length}</CountChip>
                        <IonButton
                          size="small"
                          color="danger"
                          fill="outline"
                          disabled={busy}
                          onClick={() => handleDeleteSubcollection(name)}
                        >
                          Delete all in {name}
                        </IonButton>
                      </div>
                      {docs.map((d) => (
                        <DocRow
                          key={d.path}
                          path={d.path}
                          onDelete={() =>
                            handleDeleteSinglePath(d.path)
                          }
                          busy={busy}
                        />
                      ))}
                    </div>
                  )
                )}
              </Section>

              <Section
                title="Jobs as Scheduler"
                count={summary.schedulerJobs.reduce(
                  (n, g) =>
                    n + (g.schedulerCopy ? 1 : 0) + g.werkerCopies.length,
                  0
                )}
                extra={
                  summary.schedulerJobs.length > 0 && (
                    <IonButton
                      size="small"
                      color="danger"
                      fill="outline"
                      disabled={busy}
                      onClick={handleDeleteAllSchedulerJobs}
                    >
                      Delete all scheduler jobs
                    </IonButton>
                  )
                }
              >
                {summary.schedulerJobs.map((g) => (
                  <div
                    key={g.uniqueJobId}
                    style={{
                      border: "1px solid #eee",
                      borderRadius: 6,
                      padding: 10,
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <strong>{g.jobTitle || "(untitled)"}</strong>
                      <IonNote color="medium">
                        {g.jobDate} — {g.uniqueJobId}
                      </IonNote>
                      <CountChip>
                        {(g.schedulerCopy ? 1 : 0) + g.werkerCopies.length}{" "}
                        copies
                      </CountChip>
                      <IonButton
                        size="small"
                        color="danger"
                        fill="outline"
                        disabled={busy}
                        onClick={() => handleDeleteJobGroup(g)}
                      >
                        Delete this job
                      </IonButton>
                    </div>
                    {g.schedulerCopy && (
                      <DocRow
                        path={g.schedulerCopy.path}
                        label="scheduler copy"
                        onDelete={() =>
                          handleDeleteSinglePath(g.schedulerCopy!.path)
                        }
                        busy={busy}
                      />
                    )}
                    {g.werkerCopies.length === 0 ? (
                      <IonNote color="medium">No werker copies.</IonNote>
                    ) : (
                      g.werkerCopies.map((w) => (
                        <DocRow
                          key={w.path}
                          path={w.path}
                          label={`werker: ${w.werkerName || w.werkerUid} — ${w.jobStatusWerker}`}
                          onDelete={() => handleDeleteSinglePath(w.path)}
                          busy={busy}
                        />
                      ))
                    )}
                  </div>
                ))}
              </Section>

              <Section
                title="Jobs as Werker"
                count={summary.werkerJobs.length}
                extra={
                  summary.werkerJobs.length > 0 && (
                    <IonButton
                      size="small"
                      color="danger"
                      fill="outline"
                      disabled={busy}
                      onClick={handleDeleteAllWerkerJobs}
                    >
                      Delete all werker jobs
                    </IonButton>
                  )
                }
              >
                {groupWerkerJobs(summary.werkerJobs).map(([status, docs]) => (
                  <div key={status} style={{ marginBottom: 8 }}>
                    <strong>{status}</strong>{" "}
                    <CountChip>{docs.length}</CountChip>
                    {docs.map((d) => (
                      <DocRow
                        key={d.path}
                        path={d.path}
                        label={
                          d.data?.jobInfo?.core?.jobTitle || "(untitled)"
                        }
                        onDelete={() => handleDeleteSinglePath(d.path)}
                        busy={busy}
                      />
                    ))}
                  </div>
                ))}
              </Section>

              <Section
                title="Associates (user's own userAssociations)"
                count={
                  summary.associatesDoc
                    ? Object.keys(summary.associatesDoc.data || {}).length
                    : 0
                }
                extra={
                  summary.associatesDoc && (
                    <IonButton
                      size="small"
                      color="danger"
                      fill="outline"
                      disabled={busy}
                      onClick={handleDeleteAssociatesDoc}
                    >
                      Delete entire doc
                    </IonButton>
                  )
                }
              >
                {summary.associatesDoc &&
                  Object.keys(summary.associatesDoc.data || {}).map((k) => {
                    const v: any =
                      (summary.associatesDoc?.data as any)?.[k] || {};
                    return (
                      <IonItem key={k} lines="inset">
                        <IonLabel>
                          <h3>
                            {v.firstName || ""} {v.lastName || ""}
                          </h3>
                          <p style={{ fontFamily: "monospace" }}>{k}</p>
                          <p>{v.associateStatus || ""}</p>
                        </IonLabel>
                        <IonButton
                          slot="end"
                          size="small"
                          color="danger"
                          fill="outline"
                          disabled={busy}
                          onClick={() => handleDeleteAssociateKey(k)}
                        >
                          Remove
                        </IonButton>
                      </IonItem>
                    );
                  })}
              </Section>

              <Section
                title="Chats summary doc"
                count={summary.chatsDoc ? 1 : 0}
              >
                {summary.chatsDoc && (
                  <>
                    <IonNote color="medium">
                      chats/{summary.uid} contains{" "}
                      {Object.keys(summary.chatsDoc.data || {}).length} chat
                      entries (conversation IDs).
                    </IonNote>
                    <div style={{ marginTop: 8 }}>
                      <IonButton
                        size="small"
                        color="danger"
                        fill="outline"
                        disabled={busy}
                        onClick={handleDeleteChatsDoc}
                      >
                        Delete chats summary doc
                      </IonButton>
                    </div>
                  </>
                )}
              </Section>

              <Section
                title="Announcements authored"
                count={summary.announcementsAuthored.length}
              >
                {summary.announcementsAuthored.map((a) => (
                  <DocRow
                    key={a.path}
                    path={a.path}
                    label={a.data?.title || a.data?.subject || "(untitled)"}
                    onDelete={() => handleDeleteSinglePath(a.path)}
                    busy={busy}
                  />
                ))}
              </Section>

              <Section
                title="Crews (user is member)"
                count={summary.crewsMember.length}
              >
                {summary.crewsMember.map((c) => (
                  <IonItem key={c.path} lines="inset">
                    <IonLabel>
                      <h3>{c.data?.name || c.id}</h3>
                      <p style={{ fontFamily: "monospace" }}>{c.path}</p>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      color="warning"
                      fill="outline"
                      disabled={busy}
                      onClick={() =>
                        confirmAndDelete(
                          `Remove user from crew ${c.id}`,
                          `memberIds array will have ${summary.uid} removed.`,
                          async () => {
                            await removeFromArrayField(
                              c.path,
                              "memberIds",
                              summary.uid,
                              { ...baseOpts(), action: "crewMemberRemove" }
                            );
                          }
                        )
                      }
                    >
                      Remove from memberIds
                    </IonButton>
                  </IonItem>
                ))}
              </Section>

              <Section
                title="FCM Tokens"
                count={summary.fcmTokens.length}
              >
                {summary.fcmTokens.map((t) => (
                  <DocRow
                    key={t.path}
                    path={t.path}
                    onDelete={() => handleDeleteSinglePath(t.path)}
                    busy={busy}
                  />
                ))}
              </Section>

              <Section
                title="Dangling references in OTHER users' docs"
                count={summary.danglingRefs.length}
                extra={
                  summary.danglingRefs.length > 0 && (
                    <IonButton
                      size="small"
                      color="warning"
                      fill="outline"
                      disabled={busy}
                      onClick={handleCleanupAllDangling}
                    >
                      Clean up all
                    </IonButton>
                  )
                }
              >
                {summary.danglingRefs.length === 0 ? (
                  <IonNote color="medium">None found.</IonNote>
                ) : (
                  summary.danglingRefs.map((r, i) => (
                    <IonItem key={i} lines="inset">
                      <IonIcon
                        icon={alertCircleOutline}
                        slot="start"
                        color="warning"
                      />
                      <IonLabel>
                        <h3>{r.description}</h3>
                        <p style={{ fontFamily: "monospace" }}>
                          {r.targetPath}
                        </p>
                        <p>
                          {r.cleanup.type === "deleteField"
                            ? `delete field: ${r.cleanup.fieldPath}`
                            : `arrayRemove: ${r.cleanup.fieldPath} -= ${r.cleanup.value}`}
                        </p>
                      </IonLabel>
                      <IonButton
                        slot="end"
                        size="small"
                        color="warning"
                        fill="outline"
                        disabled={busy}
                        onClick={() => handleCleanupDanglingRef(r)}
                      >
                        Clean up
                      </IonButton>
                    </IonItem>
                  ))
                )}
              </Section>

              <Section
                title="Storage files (best-effort convention scan)"
                count={summary.storageFiles.length}
                extra={
                  summary.storageFiles.length > 0 && (
                    <IonButton
                      size="small"
                      color="danger"
                      fill="outline"
                      disabled={busy}
                      onClick={handleDeleteStorage}
                    >
                      Delete all
                    </IonButton>
                  )
                }
              >
                <IonNote color="medium">
                  Scanned: profilePics/{summary.uid}/, chatAttachments/
                  &lt;chatId&gt;/, announcementAttachments/
                  &lt;announcementId&gt;/. Ad-hoc uploads may be missed.
                </IonNote>
                {summary.storageFiles.map((p) => (
                  <IonItem key={p} lines="inset">
                    <IonLabel style={{ fontFamily: "monospace" }}>
                      {p}
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      color="danger"
                      fill="outline"
                      disabled={busy}
                      onClick={() =>
                        confirmAndDelete(
                          "Delete storage file",
                          p,
                          async () => {
                            await deleteStorageFiles([p], {
                              ...baseOpts(),
                              action: "storageOne",
                            });
                          }
                        )
                      }
                    >
                      Delete
                    </IonButton>
                  </IonItem>
                ))}
              </Section>

              {/* Auth reminder */}
              <IonCard color="warning">
                <IonCardHeader>
                  <IonCardTitle>
                    <IonIcon icon={warningOutline} /> Firebase Auth account
                  </IonCardTitle>
                  <IonCardSubtitle>
                    Must be deleted manually via Firebase console
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <p>After Firestore cleanup, delete the Auth user here:</p>
                  <p>
                    <strong>UID:</strong>{" "}
                    <code>{summary.uid}</code>{" "}
                    <IonButton
                      size="small"
                      fill="clear"
                      onClick={() =>
                        navigator.clipboard?.writeText(summary.uid)
                      }
                    >
                      Copy
                    </IonButton>
                  </p>
                  {selected.email && (
                    <p>
                      <strong>Email:</strong>{" "}
                      <code>{selected.email}</code>{" "}
                      <IonButton
                        size="small"
                        fill="clear"
                        onClick={() =>
                          navigator.clipboard?.writeText(selected.email)
                        }
                      >
                        Copy
                      </IonButton>
                    </p>
                  )}
                </IonCardContent>
              </IonCard>
            </>
          )}
        </div>
      )}

      {busy && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
          }}
        >
          <IonSpinner name="crescent" color="primary" />
        </div>
      )}
    </div>
  );
};

export default DeleteUserDocs;

// ---------------------------------------------------------------------------
// Section + DocRow presentational components
// ---------------------------------------------------------------------------

const Section: React.FC<{
  title: string;
  count: number;
  extra?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, count, extra, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <IonCard>
      <IonCardHeader
        onClick={() => setOpen((v) => !v)}
        style={{ cursor: "pointer" }}
      >
        <IonCardTitle
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <span>{title}</span>
          <IonBadge color={count > 0 ? "tertiary" : "medium"}>{count}</IonBadge>
          {extra}
          <span style={{ marginLeft: "auto", fontSize: "0.8em" }}>
            {open ? "▼" : "▶"}
          </span>
        </IonCardTitle>
      </IonCardHeader>
      {open && <IonCardContent>{children}</IonCardContent>}
    </IonCard>
  );
};

const DocRow: React.FC<{
  path: string;
  label?: string;
  onDelete: () => void;
  busy: boolean;
}> = ({ path, label, onDelete, busy }) => (
  <IonItem lines="inset">
    <IonLabel>
      {label && <h3>{label}</h3>}
      <p style={{ fontFamily: "monospace", fontSize: "0.8em" }}>{path}</p>
    </IonLabel>
    <IonButton
      slot="end"
      size="small"
      color="danger"
      fill="outline"
      disabled={busy}
      onClick={onDelete}
    >
      Delete
    </IonButton>
  </IonItem>
);

/**
 * Plain styled chip for inline counts. Uses explicit colors that work in
 * both light and dark themes (avoids IonBadge's theme-dependent rendering
 * which can show as a white box in dark mode in this app).
 */
const CountChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 10,
      background: "#3880ff",
      color: "#ffffff",
      fontSize: "0.8em",
      fontWeight: 700,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const groupWerkerJobs = (
  jobs: DocEntry[]
): Array<[string, DocEntry[]]> => {
  const groups = new Map<string, DocEntry[]>();
  for (const j of jobs) {
    const status =
      (j.data?.jobInfo?.status?.jobStatusWerker as string) || "Unknown";
    if (!groups.has(status)) groups.set(status, []);
    groups.get(status)!.push(j);
  }
  return Array.from(groups.entries()).sort((a, b) =>
    a[0].localeCompare(b[0])
  );
};

// IonToggle is imported but unused in v1; kept for future dry-run placement variants.
void IonToggle;
